import { api } from "@/api/client"
import type { ApiResponse } from "@/types/api"
import type { UserDetail } from "@/types/models"

export async function getCsrfCookie(): Promise<void> {
  await api.get("/sanctum/csrf-cookie")
}

export type LoginResponse = ApiResponse<
  | { user: UserDetail }
  | { two_factor_required: true; two_factor_setup_required: boolean; two_factor_token: string }
>

export async function login(email: string, password: string, cf_turnstile_response?: string): Promise<LoginResponse> {
  await getCsrfCookie()
  const { data } = await api.post("/api/auth/login", { email, password, cf_turnstile_response })
  return data
}

export async function verifyTwoFactor(twoFactorToken: string, code: string, rememberDevice?: boolean): Promise<ApiResponse<{ user: UserDetail }>> {
  await getCsrfCookie()
  const { data } = await api.post("/api/auth/two-factor/verify", { code, remember_device: rememberDevice }, {
    headers: { Authorization: `Bearer ${twoFactorToken}` },
  })
  return data
}

export async function register(payload: {
  organization_name: string
  first_name: string
  last_name: string
  email: string
  password: string
  password_confirmation: string
  cf_turnstile_response?: string
}): Promise<ApiResponse<{ user: UserDetail }>> {
  await getCsrfCookie()
  const { data } = await api.post("/api/auth/register", payload)
  return data
}

export async function logout(): Promise<void> {
  await api.post("/api/auth/logout")
}

export async function getMe(): Promise<ApiResponse<UserDetail>> {
  const { data } = await api.get("/api/auth/me")
  return data
}

export async function forgotPassword(email: string, cf_turnstile_response?: string): Promise<ApiResponse<{ message: string }>> {
  const { data } = await api.post("/api/auth/forgot-password", { email, cf_turnstile_response })
  return data
}

export async function resetPassword(payload: {
  email: string
  token: string
  password: string
  password_confirmation: string
}): Promise<ApiResponse<{ message: string }>> {
  const { data } = await api.post("/api/auth/reset-password", payload)
  return data
}

export async function updateProfile(payload: {
  first_name?: string
  last_name?: string
}): Promise<ApiResponse<UserDetail>> {
  const { data } = await api.put("/api/auth/me", payload)
  return data
}

export async function updatePassword(payload: {
  current_password: string
  password: string
  password_confirmation: string
}): Promise<ApiResponse<{ message: string }>> {
  const { data } = await api.put("/api/auth/me/password", payload)
  return data
}

export async function updateAvatar(file: File): Promise<ApiResponse<UserDetail>> {
  const formData = new FormData()
  formData.append("_method", "PUT")
  formData.append("avatar", file)
  const { data } = await api.post("/api/auth/me", formData)
  return data
}

export async function acceptInvitation(
  token: string,
  payload: {
    first_name: string
    last_name: string
    password: string
  }
): Promise<ApiResponse<{ user: UserDetail; token: string }>> {
  await getCsrfCookie()
  const { data } = await api.post(`/api/auth/accept-invitation/${token}`, payload)
  return data
}

export async function generateQrCode(): Promise<ApiResponse<{ qr_code_token: string }>> {
  const { data } = await api.post("/api/auth/qr-code/generate")
  return data
}

export async function authenticateQrCode(
  qr_code_token: string
): Promise<ApiResponse<{ user: UserDetail; token: string }>> {
  const { data } = await api.post("/api/auth/qr-code/authenticate", { qr_code_token })
  return data
}
